import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import AdminNavBar from "../components/AdminNavbar";
import "../assets/css/apppay-payments.css"; // new CSS – all classes prefixed with 'apppay-'

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction
  ? "https://schools.luth.gov.ng"
  : "http://localhost:3003";

export default function ApplicantPayments() {
  const [data, setData] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [programmeFilter, setProgrammeFilter] = useState("All");
  const [yearFilter, setYearFilter] = useState("All");
  const [pageSize, setPageSize] = useState(25); // default 25 rows

  useEffect(() => {
    fetchPayments();
  }, []);

  async function fetchPayments() {
    try {
      const res = await fetch(`${API_BASE_URL}/api/checkapplicationinfo`);
      const json = await res.json();
      setData(Array.isArray(json) ? json : json.data || []);
    } catch (err) {
      console.error("Failed to fetch payments:", err);
    }
  }

  // Extract unique years from createdAt, sort descending (most recent first)
  const availableYears = useMemo(() => {
    const years = data
      .map((d) => d.createdAt && new Date(d.createdAt).getFullYear())
      .filter((y, i, arr) => y && arr.indexOf(y) === i)
      .sort((a, b) => b - a);
    return years;
  }, [data]);

  // Unique programmes for filter dropdown
  const programmes = useMemo(
    () => Array.from(new Set(data.map((d) => d.programme))).sort(),
    [data],
  );

  // Apply all filters
  const filtered = useMemo(() => {
    return data.filter((item) => {
      // Search (name / email)
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (
          !(
            (item.fullName || "").toLowerCase().includes(term) ||
            (item.email || "").toLowerCase().includes(term)
          )
        )
          return false;
      }
      // Payment status
      if (statusFilter !== "All" && item.paymentStatus !== statusFilter)
        return false;
      // Programme
      if (programmeFilter !== "All" && item.programme !== programmeFilter)
        return false;
      // Year (from createdAt)
      if (yearFilter !== "All") {
        const year = item.createdAt
          ? new Date(item.createdAt).getFullYear()
          : null;
        if (year !== Number(yearFilter)) return false;
      }
      return true;
    });
  }, [data, searchTerm, statusFilter, programmeFilter, yearFilter]);

  // Pagination
  const start = pageIndex * pageSize;
  const paged = filtered.slice(start, start + pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize);

  function handleSearch() {
    setSearchTerm(searchInput.trim());
    setPageIndex(0);
  }

  function exportToExcel() {
    const ws = XLSX.utils.json_to_sheet(filtered);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payments");
    XLSX.writeFile(wb, `ApplicantPayments.xlsx`);
  }

  // Reset page index when any filter changes
  useEffect(() => {
    setPageIndex(0);
  }, [statusFilter, programmeFilter, yearFilter, searchTerm, pageSize]);

  return (
    <div className="apppay-wrapper">
      <AdminNavBar />
      <div className="apppay-page">
        <main className="apppay-content">
          {/* ----- Toolbar: title + filters ----- */}
          <div className="apppay-toolbar">
            <h1 className="apppay-page-title">Applicant Payments</h1>
            <div className="apppay-filter-group">
              {/* Year filter (most recent first) */}
              <select
                className="apppay-select"
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
              >
                <option value="All">All Years</option>
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>

              {/* Programme filter */}
              <select
                className="apppay-select"
                value={programmeFilter}
                onChange={(e) => setProgrammeFilter(e.target.value)}
              >
                <option value="All">All Programmes</option>
                {programmes.map((prog) => (
                  <option key={prog} value={prog}>
                    {prog}
                  </option>
                ))}
              </select>

              {/* Status pill buttons */}
              <div className="apppay-status-buttons">
                {["All", "Paid", "Pending"].map((status) => (
                  <button
                    key={status}
                    className={`apppay-status-btn ${
                      statusFilter === status ? "active" : ""
                    }`}
                    onClick={() => setStatusFilter(status)}
                  >
                    {status}
                  </button>
                ))}
              </div>

              {/* Search input */}
              <div className="apppay-search">
                <input
                  type="text"
                  placeholder="Name or email…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="apppay-input"
                />
                <button
                  className="apppay-btn apppay-btn-primary"
                  onClick={handleSearch}
                >
                  Search
                </button>
              </div>

              {/* Export button */}
              <button
                className="apppay-btn apppay-btn-success"
                onClick={exportToExcel}
              >
                Export Excel
              </button>
            </div>
          </div>

          {/* ----- Rows per page selector ----- */}
          <div className="apppay-table-header">
            <div className="apppay-rows-control">
              <span>Rows per page:</span>
              <select
                className="apppay-select apppay-rows-select"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <span className="apppay-result-count">
              {filtered.length} applicants
            </span>
          </div>

          {/* ----- Scrollable table ----- */}
          <div className="apppay-table-container">
            <table className="apppay-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Full Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Programme</th>
                  <th>Payment</th>
                  <th>Created</th>
                  <th>School</th>
                  <th>App No</th>
                  <th>RRR</th>
                  <th>Password</th>
                </tr>
              </thead>
              <tbody>
                {paged.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="apppay-empty">
                      No applicants found
                    </td>
                  </tr>
                ) : (
                  paged.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td title={item.fullName}>{item.fullName}</td>
                      <td title={item.phone}>{item.phone}</td>
                      <td title={item.email}>{item.email}</td>
                      <td title={item.programme}>{item.programme}</td>
                      <td>
                        <span
                          className={`apppay-payment-pill ${
                            (item.paymentStatus || "")
                              .toLowerCase()
                              .includes("paid")
                              ? "paid"
                              : "pending"
                          }`}
                        >
                          {item.paymentStatus || "Unknown"}
                        </span>
                      </td>
                      <td>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString()
                          : "-"}
                      </td>
                      <td title={item.schoolName}>{item.schoolName}</td>
                      <td title={item.ApplicationNo}>{item.ApplicationNo}</td>
                      <td title={item.RRR}>{item.RRR}</td>
                      <td title={item.password}>••••••</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ----- Pagination ----- */}
          <div className="apppay-pagination">
            <button
              className="apppay-btn apppay-btn-outline"
              onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              disabled={pageIndex === 0}
            >
              Previous
            </button>
            <span className="apppay-page-info">
              Page {pageIndex + 1} of {totalPages || 1}
            </span>
            <button
              className="apppay-btn apppay-btn-outline"
              onClick={() =>
                setPageIndex((i) => Math.min(totalPages - 1, i + 1))
              }
              disabled={pageIndex >= totalPages - 1}
            >
              Next
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
